import { EmailIcon, MinusIcon } from '@chakra-ui/icons'
import { Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useAuth, usePopUpMessage } from '@hb/react'
import { ErrorWithMessage } from '@hb/shared'
import { AuthProvider, linkWithPopup, unlink, UserInfo } from 'firebase/auth'
import { useCallback, useMemo, useState } from 'react'
import { auth } from '../../backend/auth/auth'
import {
  appleAuthProvider,
  googleAuthProvider,
  passwordAuthProvider,
} from '../../backend/auth/providers'
import { handleAuthError } from '../../backend/auth/signIn'
import appleLogo from '../../icons/apple_logo_black.svg'
import googleLogo from '../../icons/google_logo.svg'
import { SvgIcon } from '../../icons/SvgIcon'
import { DeleteButton, SolidActionButton } from '../Buttons'

type ProviderId = 'google.com' | 'password' | 'apple.com'
const providerInfo: Record<
  ProviderId,
  { name: string; icon: React.ReactNode; provider: AuthProvider; canUnlink: boolean }
> = {
  'google.com': {
    name: 'Google',
    icon: <SvgIcon src={googleLogo} w={4} />,
    provider: googleAuthProvider,
    canUnlink: false,
  },
  password: {
    name: 'Email & Password',
    icon: <EmailIcon opacity={0.7} w={4} />,
    provider: passwordAuthProvider,
    canUnlink: false,
  },
  'apple.com': {
    name: 'Apple',
    icon: <SvgIcon src={appleLogo} opacity={0.6} w={4} />,
    provider: appleAuthProvider,
    canUnlink: true,
  },
}

const linkProvider = async (provider: AuthProvider, showError: (message: string) => void) => {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Not signed in')
  }
  try {
    await linkWithPopup(currentUser, provider)
    await currentUser.reload()
  } catch (err) {
    handleAuthError(auth, err as ErrorWithMessage, showError)
  }
}
const unlinkProvider = async (provider: ProviderId) => {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Not signed in')
  }
  await unlink(currentUser, provider)
  await currentUser.reload()
}

const allProviderIds = Object.keys(providerInfo) as ProviderId[]

const ProviderInfo = ({
  providerId,
  providerData,
}: {
  providerId: ProviderId
  providerData: UserInfo | undefined
}) => {
  const { showError } = usePopUpMessage()
  const [isLinking, setIsLinking] = useState(false)
  const provider = useMemo(() => providerInfo[providerId], [providerId])
  const handleUnlinkProvider = useCallback(async () => {
    try {
      await unlinkProvider(providerId)
    } catch (error) {
      showError(
        `Error unlinking provider: ${(error as ErrorWithMessage).message || 'Unknown error'}`,
      )
    }
  }, [showError, providerId])
  const handleLinkProvider = useCallback(async () => {
    try {
      setIsLinking(true)
      await linkProvider(provider.provider, showError)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLinking(false)
    }
  }, [showError, provider])
  if (!provider) return null
  return (
    <Flex px={2} py={1} align="center" justify="space-between" w="100%">
      <Flex gap={2} w="100%" align="center">
        {provider.icon}
        <Text fontWeight={500} color="gray.600">
          {provider.name}
        </Text>
      </Flex>
      {providerData ? (
        <Flex gap={2} align="center">
          {provider.canUnlink ? (
            <DeleteButton
              itemName={provider.name}
              onDelete={handleUnlinkProvider}
              bg="red.100"
              actionName="Unlink"
              icon={<MinusIcon />}
              size="xs"
              borderRadius="full"
            />
          ) : null}
          <Text
            color="green.600"
            fontWeight={500}
            fontSize="sm"
            borderColor="green.600"
            border="1px solid"
            px={2}
            borderRadius="full">
            LINKED
          </Text>
        </Flex>
      ) : (
        <SolidActionButton
          isLoading={isLinking}
          size="xs"
          borderRadius="full"
          onClick={handleLinkProvider}>
          LINK
        </SolidActionButton>
      )}
    </Flex>
  )
}

export const SignInMethodsView = () => {
  const authUser = useAuth(s => s.authUser)
  const { providerData = [] } = authUser ?? {}
  const providerDataMap = useMemo(
    () =>
      providerData.reduce<Partial<Record<ProviderId, UserInfo>>>((acc, provider) => {
        acc[provider.providerId as ProviderId] = provider
        return acc
      }, {}),
    [providerData],
  )

  return (
    <Flex bg="white" w="100%" flexFlow="column">
      <HStack borderBottomRadius={4} p={2} bg="gray.50" w="100%" borderBottom="1px solid #cdcdcd">
        <Text fontSize="xs" fontWeight={500} fontFamily="hero-new">
          SIGN IN METHODS
        </Text>
      </HStack>
      <VStack divider={<Divider />} w="100%" spacing={0}>
        {allProviderIds.map(provider => (
          <ProviderInfo
            key={provider}
            providerId={provider}
            providerData={providerDataMap[provider]}
          />
        ))}
      </VStack>
    </Flex>
  )
}
